export const FREE_MEMORY_CAPABILITIES = [
	"payment_trace_capture",
	"manual_csv_and_receipt_capture",
	"receipt_and_memory_storage",
	"catalog_unlock_and_browse",
	"memo_mood_context_and_favorite",
	"weekly_story_and_memory_timeline",
	"weekly_monthly_and_annual_basic_summary",
	"csv_json_export_and_encrypted_backup",
	"restore_trash_and_delete",
] as const;

export type FreeMemoryCapability = (typeof FREE_MEMORY_CAPABILITIES)[number];

export const ARCHIVE_OFFERS = {
	lifetime: {
		sku: "lifetime",
		grossPriceKrw: 9_900,
		billing: "non_consumable",
	},
	annual_report: {
		sku: "annual_report",
		grossPriceKrw: 2_900,
		billing: "one_time_report",
	},
} as const;

export type ArchiveOfferDecision =
	| { renderPaywall: true; reason: "eligible_explicit_archive_request" }
	| {
			renderPaywall: false;
			reason:
				| "already_entitled"
				| "commercial_release_not_approved"
				| "initial_free_cohort_not_complete"
				| "payment_unavailable"
				| "first_value_not_completed"
				| "not_explicitly_requested";
	  };

/**
 * Paid archive offers are deliberately post-value and user initiated. The first
 * free cohort can exercise every core memory capability without seeing a
 * paywall, even when a provider binding or client flag is accidentally enabled.
 */
export function decideArchiveOffer(input: {
	hasArchiveEntitlement: boolean;
	firstValueCompleted: boolean;
	explicitArchiveRequest: boolean;
	initialFreeCohortComplete: boolean;
	commercialReleaseApproved: boolean;
	paymentAvailable: boolean;
}): ArchiveOfferDecision {
	if (input.hasArchiveEntitlement)
		return { renderPaywall: false, reason: "already_entitled" };
	if (!input.commercialReleaseApproved)
		return {
			renderPaywall: false,
			reason: "commercial_release_not_approved",
		};
	if (!input.initialFreeCohortComplete)
		return {
			renderPaywall: false,
			reason: "initial_free_cohort_not_complete",
		};
	if (!input.paymentAvailable)
		return { renderPaywall: false, reason: "payment_unavailable" };
	if (!input.firstValueCompleted)
		return { renderPaywall: false, reason: "first_value_not_completed" };
	if (!input.explicitArchiveRequest)
		return { renderPaywall: false, reason: "not_explicitly_requested" };
	return {
		renderPaywall: true,
		reason: "eligible_explicit_archive_request",
	};
}

export function keepsFreeMemoryCapability(
	capability: FreeMemoryCapability,
	_entitlementState: "none" | "active" | "refunded",
): boolean {
	return FREE_MEMORY_CAPABILITIES.includes(capability);
}
